import React, { useState } from 'react';
import {
  FUNDING,
  PayPalButtons,
  PayPalScriptProvider,
} from '@paypal/react-paypal-js';
import { PayPalOrder } from '../../../interface/apiServiceInterface';
import { updateContactInfo } from '../../button-section/applePay-button/paymentHelper';
import { postUpsellPaypalPurchasePayload } from '../../../interface/postUpsellInterface';
import checkoutService from '../../../api/checkoutService';
import { ErrorResponse } from 'react-router-dom';
import { ReduxDispatchProps } from '../../../services/constants';
import { setErrorStatus } from '../../../redux/slices/postUpsellStateSlice';
import { mapPercentageToRange } from '../../../../page-builder/utils/helper';
import { Shop } from '../../../interface/storeInterface';

interface PostUpsellCartProps {
  totalAmount: string;
  cartId: string;
  currencyCode: string;
}

interface PostUpsellPayPalProps extends ReduxDispatchProps {
  selectedItems: {
    variantId: string;
    GroupId: string;
    IsDownsell: boolean;
    DownsellId?: string;
    ProductId: string;
  }[];
  buttonSize: React.CSSProperties;
  handleClick: () => Promise<void>;
  purchaseToken: string;
  storeData: Shop;
  navigateToConfirmationPage: () => void;
  smartUpsellFunnelID: string;
  isNavToThankyouPage?: boolean;
}

const PostUpsellPayPal: React.FC<PostUpsellPayPalProps> = ({
  selectedItems,
  purchaseToken,
  storeData,
  navigateToConfirmationPage,
  smartUpsellFunnelID,
  useAppDispatch,
  handleClick,
  isNavToThankyouPage,
  buttonSize,
}) => {
  const [cardData, setCardData] = useState<PostUpsellCartProps>(
    {} as PostUpsellCartProps,
  );
  const dispatch = useAppDispatch();
  const initialOptions = {
    clientId: storeData?.PaypalClientId || '',
    currency: cardData.currencyCode || 'USD',
    intent: 'capture',
  };

  const createOrder = async (_data: unknown, actions: unknown) => {
    const paypalActions = actions as {
      order: { create: (_options: unknown) => Promise<string> };
    };

    try {
      const { totalAmount, cartId, currencyCode } =
        await CreateUpsellProductCard();
      setCardData({ totalAmount, cartId, currencyCode });

      return await paypalActions.order.create({
        purchase_units: [{ amount: { value: totalAmount } }],
        application_context: {
          shipping_preference: 'NO_SHIPPING',
        },
      });
    } catch (error) {
      console.error('Error creating order', error);
      throw error;
    }
  };

  const onApprove = async (_data: unknown, actions: unknown) => {
    try {
      const paypalActions = actions as {
        order?: { capture: () => Promise<PayPalOrder> };
      };
      if (!paypalActions.order)
        throw new Error('PayPal order action is missing');
      const details: PayPalOrder = await paypalActions.order.capture();
      await postUpsellPurchase(details);
    } catch (error) {
      console.error('PayPal capture error:', error);
    }
  };
  const postUpsellPurchase = async (paypalDetails: PayPalOrder) => {
    if (!cardData.cartId) {
      console.error('Missing cart ID. Cannot proceed.');
      return;
    }
    const payer = paypalDetails?.payer;
    const contactPayload = {
      Email: payer?.email_address || '',
      PhoneNumber: '',
      ID: cardData.cartId,
      ShopID: storeData?.ShopID || '',
      StoreID: Number(storeData.StoreID) || 0,
    };

    await updateContactInfo(contactPayload, storeData.StoreID || '');
    const requestPayload = {
      purchase_units: [
        {
          amount: {
            value: paypalDetails?.purchase_units?.[0]?.amount?.value || '0.00',
            currency_code:
              paypalDetails?.purchase_units?.[0]?.amount?.currency_code ||
              'USD',
          },
        },
      ],
      application_context: {
        shipping_preference: 'NO_SHIPPING',
      },
    };
    const request_string = JSON.stringify(requestPayload);
    const ResponseString = JSON.stringify(paypalDetails);
    const payload: postUpsellPaypalPurchasePayload = {
      CartId: cardData.cartId,
      LineItems: selectedItems
        .filter(item => item)
        .map(item => ({
          Quantity: 1,
          VariantID: item.variantId,
          UpsellDetails: {
            GroupId: item.GroupId,
            IsDownsell: item.IsDownsell,
            DownsellId: item.IsDownsell ? (item.DownsellId ?? '') : '',
            ProductId: item.ProductId,
          },
        })),
      PurchaseToken: purchaseToken,
      ShopID: storeData?.ShopID || '',
      FunnelId: Number(smartUpsellFunnelID),
      StoreID: storeData?.StoreID || '',
      paypalPayload: {
        orderId: paypalDetails.id || '',
        transactionId:
          paypalDetails.purchase_units?.[0]?.payments?.captures?.[0].id || '',
        status: paypalDetails.status || '',
        email: payer?.email_address || '',
        request_string: request_string,
        ResponseString: ResponseString,
      },
    };

    try {
      const res = await checkoutService.postUpsellPurchase(payload);

      if (res.status === 200) {
        if (isNavToThankyouPage) {
          navigateToConfirmationPage();
        } else {
          handleClick();
        }
      }
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      if (err.status === 400) {
        if (isNavToThankyouPage) {
          navigateToConfirmationPage();
        }
        dispatch(setErrorStatus(err.status));
        throw error;
      } else {
        if (isNavToThankyouPage) navigateToConfirmationPage();
        throw error;
      }
    }
  };
  const CreateUpsellProductCard = async (): Promise<{
    totalAmount: string;
    cartId: string;
    currencyCode: string;
  }> => {
    const payload: postUpsellPaypalPurchasePayload = {
      LineItems: selectedItems
        .filter(item => item)
        .map(item => ({
          Quantity: 1,
          VariantID: item.variantId,
          UpsellDetails: {
            GroupId: item.GroupId,
            IsDownsell: item.IsDownsell,
            DownsellId: item.IsDownsell ? (item.DownsellId ?? '') : '',
            ProductId: item.ProductId,
          },
        })),
      PurchaseToken: purchaseToken,
      ShopID: storeData?.ShopID || '',
      FunnelId: Number(smartUpsellFunnelID),
      StoreID: storeData?.StoreID || '',
    };
    try {
      const res = await checkoutService.CreateUpsellCart(payload);
      if (res.status === 200) {
        const { TotalAmount, ID, currencyCode } = res.data.Result;
        return {
          totalAmount: TotalAmount,
          cartId: ID,
          currencyCode: currencyCode,
        };
      }
    } catch (error) {
      console.error('Error creating upsell cart:', error);
    }

    throw new Error('Failed to create upsell cart');
  };

  return (
    <>
      <PayPalScriptProvider options={initialOptions}>
        <PayPalButtons
          createOrder={createOrder}
          style={{
            height: buttonSize.height?.toString().includes('px')
              ? parseInt(buttonSize.height.toString()) < 25
                ? 25
                : parseInt(buttonSize.height.toString()) > 55
                  ? 55
                  : parseInt(buttonSize.height.toString())
              : mapPercentageToRange(buttonSize?.height?.toString() || '100%'),
          }}
          onApprove={onApprove}
          fundingSource={FUNDING.PAYPAL}
        />
      </PayPalScriptProvider>
    </>
  );
};

export default PostUpsellPayPal;
